import { Component, ChangeDetectionStrategy, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { ContactCandidateService } from '../../services/contact-candidate.service';

/**
 * Globalne okno „Napisz do kandydata" — montowane raz w layoucie aplikacji,
 * sterowane przez {@link ContactCandidateService}. Każdy ekran firmy otwiera je
 * przez `contactService.open({ candidateId, name })`.
 */
@Component({
  selector: 'app-contact-candidate-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    TextareaModule,
    ToastModule,
    TranslocoModule,
  ],
  providers: [MessageService],
  templateUrl: './contact-candidate-dialog.component.html',
  styleUrl: './contact-candidate-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContactCandidateDialogComponent {
  private readonly contact = inject(ContactCandidateService);
  private readonly messageService = inject(MessageService);
  private readonly transloco = inject(TranslocoService);

  readonly target = this.contact.target;
  readonly sending = this.contact.sending;
  readonly open = computed(() => this.target() !== null);

  readonly subject = signal('');
  readonly message = signal('');

  readonly canSend = computed(() => this.message().trim().length > 0 && !this.sending());

  onVisibleChange(visible: boolean): void {
    if (!visible) {
      this.contact.close();
      // Reset dopiero gdy okno faktycznie zamknięte (close() respektuje sending).
      if (this.target() === null) {
        this.subject.set('');
        this.message.set('');
      }
    }
  }

  onSend(): void {
    if (!this.canSend()) {
      return;
    }
    this.contact.send(
      this.message(),
      this.subject().trim() || undefined,
      result => {
        this.messageService.add({
          severity: 'success',
          summary: this.transloco.translate(result === 'reused' ? 'chat.contact.reused' : 'chat.contact.sent'),
        });
        this.subject.set('');
        this.message.set('');
      },
      () => {
        this.messageService.add({
          severity: 'error',
          summary: this.transloco.translate('chat.contact.error'),
        });
      },
    );
  }
}
